import { Pencil, Trash2, Copy } from "lucide-react";
import { Link } from "react-router-dom";


const TYPE_STYLES = {
  flat_rate: "border-l-2 border-amber-400",
  unit_based: "border-l-2 border-teal-400",
};

const TYPE_BADGE = {
  flat_rate: { label: "Flat", cls: "bg-amber-100 text-amber-700" },
  unit_based: { label: "Unit", cls: "bg-teal-100 text-teal-700" },
};

const ReceivablesTable = ({
  data = [],
  onEdit,
  onDelete,
  onClone,
  selectedIds = [],
  onToggle,
  onToggleAll,
}) => {
  const formatLanguagePair = (lp) => {
    if (!lp) return "—";
    const src = lp.sourceLanguage?.name || lp.sourceLanguage?.code || "";
    const tgt = lp.targetLanguage?.name || lp.targetLanguage?.code || "";
    return src && tgt ? `${src} → ${tgt}` : "—";
  };

  const formatCurrency = (c) => c?.currency?.code || "—";

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const date = new Date(d);
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    } catch {
      return "—";
    }
  };

  const fmt = (v, fallback = "—") =>
    v !== undefined && v !== null && v !== "" ? v : fallback;

  const fmtNum = (v) =>
    v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

  const eligible = data.filter((r) => !r.invoice);
  const allSelected = eligible.length > 0 && eligible.every((r) => selectedIds.includes(`${r.type}-${r.id}`));
  const someSelected = data.some((r) => selectedIds.includes(`${r.type}-${r.id}`));

  const HEADERS = [
    "checkbox",
    "Type",
    "Code",
    "Invoice",
    "PO Number",
    "Service",
    "Language Pair",
    "Unit Amount",
    "Unit",
    "Price / Unit",
    "Subtotal",
    "Extra",
    "Discount",
    "Total",
    "Currency",
    "Filename",
    "Internal Note",
    "Issued At",
    "Actions",
  ];

  const COL_WIDTHS = [
    "2%",   // checkbox
    "4%",   // Type
    "4%",   // Code
    "4%",   // Invoice
    "7%",   // PO Number
    "9%",   // Service
    "11%",  // Language Pair
    "4%",   // Unit Amount
    "4%",   // Unit
    "5%",   // Price / Unit
    "4%",   // Subtotal
    "5%",   // Extra
    "4%",   // Discount
    "3%",   // Total
    "5%",   // Currency
    "10%",  // Filename
    "7%",   // Internal Note
    "6%",   // Issued At
    "7%",   // Actions
  ];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-amber-400" />
          Flat Rate
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-teal-400" />
          Unit Based
        </span>
        {selectedIds.length > 0 && (
          <span className="ml-auto text-blue-600 font-medium">
            {selectedIds.length} selected
          </span>
        )}
      </div>

      <div className="max-h-[460px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1400px]">
          <colgroup>
            {COL_WIDTHS.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              <th className="px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onToggleAll?.(eligible)}
                  className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                />
              </th>
              {HEADERS.slice(1).map((h) => (
                <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                  No receivables found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowKey = `${row.type}-${row.id}`;
                const isInvoiced = !!row.invoice;
                const isSelected = selectedIds.includes(rowKey);
                const isFlat = row.type === "flat_rate";
                const badge = TYPE_BADGE[row.type] ?? TYPE_BADGE.flat_rate;
                const typeStyle = TYPE_STYLES[row.type] ?? "";
                const rowBg = isInvoiced
                  ? "bg-gray-100 opacity-70"
                  : isSelected
                  ? "bg-blue-50"
                  : index % 2 === 0
                  ? "bg-gray-50"
                  : "bg-white";

                return (
                  <tr
                    key={rowKey}
                    className={`${rowBg} ${typeStyle} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInvoiced}
                        onChange={() => !isInvoiced && onToggle?.(rowKey)}
                        className="w-3.5 h-3.5 accent-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <Link
                        to={`/client/receivable/${row.id}?type=${row.type}`}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        ACR{row.id}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      {row.invoice ? (
                        <Link
                          to={`/client/invoice/${row.invoice.id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {row.invoice.invoice_code}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {fmt(row.po_number)}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.service?.name || fmt(row.service)}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {formatLanguagePair(row.languagePair)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : fmt(row.unit_amount)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : (row.unit?.name || fmt(row.unit))}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : fmt(row.price_per_unit)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {fmtNum(row.subtotal)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {fmtNum(row.extra_charge)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {fmtNum(row.discount)}
                    </td>

                    <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                      {fmtNum(row.total)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatCurrency(row.currency)}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.file?.file_name || row.file?.file_code || fmt(row.file_name)}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-600 break-words">
                      {fmt(row.internal_note)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatDate(row.issued_at || row.createdAt)}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-3">
                        <Copy
                          className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                          title="Clone"
                          onClick={() => onClone?.(row)}
                        />
                        <Pencil
                          className={`w-4 h-4 transition-colors ${isInvoiced ? "text-gray-200 cursor-not-allowed" : "text-blue-400 hover:text-blue-600 cursor-pointer"}`}
                          title={isInvoiced ? "Cannot edit an invoiced receivable" : "Edit"}
                          onClick={() => !isInvoiced && onEdit?.(row)}
                        />
                        <Trash2
                          className={`w-4 h-4 transition-colors ${isInvoiced ? "text-gray-200 cursor-not-allowed" : "text-red-400 hover:text-red-600 cursor-pointer"}`}
                          title={isInvoiced ? "Cannot delete an invoiced receivable" : "Delete"}
                          onClick={() => !isInvoiced && onDelete?.(row)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReceivablesTable;
